import { useState } from 'react';
import { FileText, ChevronRight, ExternalLink, FileX2, Minus, Plus } from 'lucide-react';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';

interface Props {
  pdfUrl: string | null;
  /** Página do ponto que está sendo precificado. */
  pagina: number | null;
  nomeMaquina: string;
  /** Rótulo da parte, só para o cabeçalho do painel. */
  contexto: string;
}

const ALTURAS = [420, 640, 900];

/**
 * Preview da AR original, aberta na página do ponto que está na tela.
 *
 * Serve para conferir o que o documento diz antes de fechar o preço — a foto e
 * o texto extraídos são um recorte, e às vezes a decisão depende do resto da
 * página (o parecer técnico, os itens da norma, o quadro de categoria).
 *
 * Usa o visualizador nativo do navegador via `#page=N`. Como trocar só o hash
 * não faz o iframe pular de página, o `key` força a remontagem a cada mudança
 * de ponto — é o que garante que ele abra sempre na página certa.
 */
export function PreviewAr({ pdfUrl, pagina, nomeMaquina, contexto }: Props) {
  const [aberto, setAberto] = useState(false);
  const [nivelAltura, setNivelAltura] = useState(0);

  if (!pdfUrl) {
    return (
      <div className="mt-4 bg-white dark:bg-schmersal-gray-darker rounded-xl p-4 flex items-center gap-2 text-sm text-schmersal-gray-medium">
        <FileX2 className="h-4 w-4 flex-shrink-0" />
        Esta máquina foi importada antes de o sistema guardar a AR original. Reimporte-a para ver o documento aqui.
      </div>
    );
  }

  const base = toAbsoluteFotoUrl(pdfUrl);
  const src = `${base}#page=${pagina ?? 1}&view=FitH`;
  const altura = ALTURAS[nivelAltura];

  return (
    <div className="mt-4 bg-white dark:bg-schmersal-gray-darker rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          onClick={() => setAberto((v) => !v)}
          className="flex items-center gap-2 min-w-0 text-left"
        >
          <ChevronRight className={`h-4 w-4 text-schmersal-gray-medium transition-transform flex-shrink-0 ${aberto ? 'rotate-90' : ''}`} />
          <FileText className="h-4 w-4 text-schmersal-blue dark:text-schmersal-turquoise flex-shrink-0" />
          <span className="text-sm font-semibold text-schmersal-blue dark:text-white truncate">
            Apreciação de Risco original
          </span>
          <span className="text-xs text-schmersal-gray-medium truncate hidden sm:inline">
            · {contexto}{pagina ? ` · página ${pagina}` : ''}
          </span>
        </button>

        <div className="flex items-center gap-1 flex-shrink-0">
          {aberto && (
            <>
              <button
                onClick={() => setNivelAltura((n) => Math.max(0, n - 1))}
                disabled={nivelAltura === 0}
                title="Diminuir o painel"
                className="p-1.5 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setNivelAltura((n) => Math.min(ALTURAS.length - 1, n + 1))}
                disabled={nivelAltura === ALTURAS.length - 1}
                title="Aumentar o painel"
                className="p-1.5 rounded-lg text-schmersal-gray-dark dark:text-schmersal-gray-light hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark disabled:opacity-30"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            title="Abrir a AR em outra aba"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-schmersal-gray-dark dark:text-schmersal-gray-light hover:text-schmersal-blue dark:hover:text-schmersal-turquoise"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Abrir</span>
          </a>
        </div>
      </div>

      {aberto && (
        <iframe
          // Remonta a cada troca de página: mudar só o hash não reposiciona o
          // visualizador nativo do navegador.
          key={`${pdfUrl}#${pagina ?? 1}`}
          src={src}
          title={`AR de ${nomeMaquina}`}
          style={{ height: altura }}
          className="w-full border-t border-schmersal-gray-light dark:border-schmersal-gray-dark bg-schmersal-gray-light dark:bg-schmersal-gray-darkest"
        />
      )}
    </div>
  );
}
